/**
 * GrapesJS Keyboard Shortcuts
 * Enhanced keyboard shortcuts with help modal
 */

class KeyboardShortcuts {
  constructor(editor) {
    this.editor = editor;
    this.shortcuts = {
      // Editing shortcuts
      'ctrl+c': { command: 'core:copy', label: 'Copy', category: 'Edit' },
      'ctrl+v': { command: 'core:paste', label: 'Paste', category: 'Edit' },
      'ctrl+x': { command: 'tlb-delete', label: 'Cut', category: 'Edit' },
      // Note: delete/backspace are NOT mapped here because GrapesJS handles
      // them natively and correctly yields to contenteditable during inline
      // text editing. Explicit keymaps would override that and delete the
      // entire component instead of just a character.

      // Undo/Redo
      'ctrl+z': { command: 'core:undo', label: 'Undo', category: 'History' },
      'ctrl+shift+z': { command: 'core:redo', label: 'Redo', category: 'History' },
      'ctrl+y': { command: 'core:redo', label: 'Redo', category: 'History' },

      // Selection
      'ctrl+a': { command: 'select-all', label: 'Select All', category: 'Selection' },
      'escape': { command: 'deselect-all', label: 'Deselect', category: 'Selection' },

      // View
      'shift+g': { command: 'toggle-grid', label: 'Toggle Grid', category: 'View' },
      'shift+s': { command: 'toggle-snap', label: 'Toggle Snap', category: 'View' },

      // Help
      'shift+/': { command: 'show-shortcuts', label: 'Show Shortcuts', category: 'Help' },
      '?': { command: 'show-shortcuts', label: 'Show Shortcuts', category: 'Help' },
    };

    this.init();
  }

  /**
   * Initialize keyboard shortcuts
   */
  init() {
    this.registerCustomCommands();
    this.registerShortcuts();
    this.addHelpButton();
  }

  /**
   * Register custom commands
   */
  registerCustomCommands() {
    const commands = this.editor.Commands;

    // Select all command
    commands.add('select-all', {
      run: (editor) => {
        const wrapper = editor.getWrapper();
        const allComponents = wrapper.find('*');
        editor.select(allComponents);
      },
    });

    // Deselect all command
    commands.add('deselect-all', {
      run: (editor) => {
        editor.selectRemove();
      },
    });

    // Toggle grid command
    commands.add('toggle-grid', {
      run: (editor) => {
        if (window.canvasImprovements && window.canvasImprovements.grid) {
          window.canvasImprovements.grid.toggleGrid();
        }
      },
    });

    // Toggle snap command
    commands.add('toggle-snap', {
      run: (editor) => {
        if (window.canvasImprovements && window.canvasImprovements.grid) {
          window.canvasImprovements.grid.toggleSnap();
        }
      },
    });

    // Show shortcuts help command
    commands.add('show-shortcuts', {
      run: () => {
        this.showHelp();
      },
    });
  }

  /**
   * Register keyboard shortcuts with GrapesJS
   */
  registerShortcuts() {
    const keymaps = this.editor.Keymaps;

    Object.entries(this.shortcuts).forEach(([keys, data]) => {
      try {
        keymaps.add(data.command, keys, data.command);
      } catch (error) {
        // Command might already be registered, ignore
      }
    });
  }

  /**
   * Add help button to toolbar
   */
  addHelpButton() {
    const panel = this.editor.Panels.getPanel('options');

    if (!panel) {
      // Options panel not found
      return;
    }

    panel.get('buttons').add({
      id: 'shortcuts-help',
      className: 'fa fa-keyboard-o',
      command: 'show-shortcuts',
      attributes: {
        title: 'Keyboard Shortcuts (?)',
        'data-tooltip': 'Show keyboard shortcuts help'
      },
    });
  }

  /**
   * Show keyboard shortcuts help modal
   */
  showHelp() {
    // Check if modal already exists
    let modal = document.getElementById('gjs-shortcuts-modal');

    if (modal) {
      modal.style.display = 'flex';
      return;
    }

    // Create modal
    modal = document.createElement('div');
    modal.id = 'gjs-shortcuts-modal';
    modal.className = 'gjs-shortcuts-modal';
    modal.innerHTML = this.generateHelpHTML();

    // Add to page
    document.body.appendChild(modal);

    // Add event listeners
    this.addModalEventListeners(modal);
  }

  /**
   * Generate help modal HTML
   * @returns {string} HTML markup
   */
  generateHelpHTML() {
    // Group shortcuts by category
    const groupedShortcuts = {};

    Object.entries(this.shortcuts).forEach(([keys, data]) => {
      const category = data.category || 'Other';
      if (!groupedShortcuts[category]) {
        groupedShortcuts[category] = [];
      }
      groupedShortcuts[category].push({ keys, ...data });
    });

    // Generate HTML
    let categoriesHTML = '';

    Object.entries(groupedShortcuts).forEach(([category, shortcuts]) => {
      categoriesHTML += `
        <div class="gjs-shortcuts-category">
          <h4>${category}</h4>
          <table>
            ${shortcuts.map((shortcut) => `
              <tr>
                <td class="gjs-shortcuts-keys">
                  ${this.formatKeys(shortcut.keys)}
                </td>
                <td class="gjs-shortcuts-label">${shortcut.label}</td>
              </tr>
            `).join('')}
          </table>
        </div>
      `;
    });

    return `
      <div class="gjs-shortcuts-overlay"></div>
      <div class="gjs-shortcuts-content">
        <div class="gjs-shortcuts-header">
          <h3>Keyboard Shortcuts</h3>
          <button class="gjs-shortcuts-close" aria-label="Close">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="gjs-shortcuts-body">
          ${categoriesHTML}
        </div>
        <div class="gjs-shortcuts-footer">
          <p>Press <kbd>?</kbd> or <kbd>Shift</kbd> + <kbd>/</kbd> to toggle this help</p>
        </div>
      </div>
    `;
  }

  /**
   * Format keyboard shortcut keys for display
   * @param {string} keys - Raw key combination
   * @returns {string} Formatted HTML
   */
  formatKeys(keys) {
    // Replace modifiers with platform-appropriate symbols
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlSymbol = isMac ? '⌘' : 'Ctrl';

    return keys
      .split('+')
      .map((key) => {
        let displayKey = key;
        if (key === 'ctrl') displayKey = ctrlSymbol;
        if (key === 'shift') displayKey = 'Shift';
        if (key === 'alt') displayKey = isMac ? '⌥' : 'Alt';
        return `<kbd>${displayKey}</kbd>`;
      })
      .join(' <span class="gjs-shortcuts-plus">+</span> ');
  }

  /**
   * Add event listeners to modal
   * @param {HTMLElement} modal - Modal element
   */
  addModalEventListeners(modal) {
    // Close button
    const closeBtn = modal.querySelector('.gjs-shortcuts-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        modal.style.display = 'none';
      });
    }

    // Overlay click
    const overlay = modal.querySelector('.gjs-shortcuts-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        modal.style.display = 'none';
      });
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.style.display === 'flex') {
        modal.style.display = 'none';
      }
    });
  }

  /**
   * Get all registered shortcuts
   * @returns {Object} Shortcuts object
   */
  getShortcuts() {
    return this.shortcuts;
  }

  /**
   * Add a custom shortcut
   * @param {string} keys - Key combination
   * @param {string} command - Command to execute
   * @param {string} label - Display label
   * @param {string} category - Category for help modal
   */
  addShortcut(keys, command, label, category = 'Custom') {
    this.shortcuts[keys] = { command, label, category };

    // Register with GrapesJS
    try {
      this.editor.Keymaps.add(command, keys, command);
    } catch (error) {
      // shortcut already registered
    }
  }
}

// Add modal CSS styles
const modalStyles = document.createElement('style');
modalStyles.textContent = `
  .gjs-shortcuts-modal {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: var(--z-modal, 1050);
    align-items: center;
    justify-content: center;
    font-family: var(--font-sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  }

  .gjs-shortcuts-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
  }

  .gjs-shortcuts-content {
    position: relative;
    background: var(--color-bg-panel, #ffffff);
    border-radius: var(--radius-xl, 12px);
    box-shadow: var(--shadow-2xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25));
    max-width: 800px;
    max-height: 80vh;
    width: 90%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: scaleIn 0.2s ease-out;
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .gjs-shortcuts-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-6, 24px);
    border-bottom: 1px solid var(--color-border-light, #e5e5e5);
  }

  .gjs-shortcuts-header h3 {
    margin: 0;
    font-size: var(--text-2xl, 20px);
    font-weight: var(--font-semibold, 600);
    color: var(--color-text-primary, #1f2937);
  }

  .gjs-shortcuts-close {
    background: transparent;
    border: none;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-md, 6px);
    cursor: pointer;
    color: var(--color-text-secondary, #6b7280);
    transition: all var(--transition-fast, 150ms);
  }

  .gjs-shortcuts-close:hover {
    background: var(--color-bg-hover, #f0f0f2);
    color: var(--color-text-primary, #1f2937);
  }

  .gjs-shortcuts-body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-6, 24px);
  }

  .gjs-shortcuts-category {
    margin-bottom: var(--space-8, 32px);
  }

  .gjs-shortcuts-category:last-child {
    margin-bottom: 0;
  }

  .gjs-shortcuts-category h4 {
    margin: 0 0 var(--space-4, 16px) 0;
    font-size: var(--text-lg, 16px);
    font-weight: var(--font-semibold, 600);
    color: var(--color-primary, #4e5d9d);
  }

  .gjs-shortcuts-category table {
    width: 100%;
    border-collapse: collapse;
  }

  .gjs-shortcuts-category tr {
    border-bottom: 1px solid var(--color-border-light, #e5e5e5);
  }

  .gjs-shortcuts-category tr:last-child {
    border-bottom: none;
  }

  .gjs-shortcuts-category td {
    padding: var(--space-3, 12px) 0;
  }

  .gjs-shortcuts-keys {
    width: 40%;
    font-family: var(--font-mono, monospace);
  }

  .gjs-shortcuts-keys kbd {
    display: inline-block;
    padding: 4px 8px;
    background: var(--color-bg-hover, #f0f0f2);
    border: 1px solid var(--color-border-light, #e5e5e5);
    border-radius: var(--radius-sm, 4px);
    font-size: var(--text-xs, 11px);
    font-weight: var(--font-semibold, 600);
    color: var(--color-text-primary, #1f2937);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  }

  .gjs-shortcuts-plus {
    color: var(--color-text-muted, #9ca3af);
    font-size: var(--text-xs, 11px);
    margin: 0 2px;
  }

  .gjs-shortcuts-label {
    font-size: var(--text-base, 14px);
    color: var(--color-text-primary, #1f2937);
  }

  .gjs-shortcuts-footer {
    padding: var(--space-4, 16px) var(--space-6, 24px);
    border-top: 1px solid var(--color-border-light, #e5e5e5);
    background: var(--color-bg-canvas, #f8f8fa);
  }

  .gjs-shortcuts-footer p {
    margin: 0;
    font-size: var(--text-sm, 12px);
    color: var(--color-text-secondary, #6b7280);
    text-align: center;
  }

  /* Dark mode */
  [data-theme="dark"] .gjs-shortcuts-content {
    background: var(--color-bg-panel, #242424);
  }

  [data-theme="dark"] .gjs-shortcuts-header h3 {
    color: var(--color-text-primary, #e5e5e5);
  }

  [data-theme="dark"] .gjs-shortcuts-keys kbd {
    background: var(--color-bg-hover, #2e2e2e);
    border-color: var(--color-border-light, #3a3a3a);
    color: var(--color-text-primary, #e5e5e5);
  }

  [data-theme="dark"] .gjs-shortcuts-label {
    color: var(--color-text-primary, #e5e5e5);
  }

  [data-theme="dark"] .gjs-shortcuts-footer {
    background: var(--color-bg-hover, #2e2e2e);
  }
`;

document.head.appendChild(modalStyles);

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = KeyboardShortcuts;
} else {
  window.KeyboardShortcuts = KeyboardShortcuts;
}
